import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const UsersListLayer = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [perPage, setPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentUsername, setCurrentUsername] = useState('');
    const [currentUserRole, setCurrentUserRole] = useState('');
    const [currentUserId, setCurrentUserId] = useState(null);
    const [roles, setRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [canAddUser, setCanAddUser] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0,
    });

    // Get current user info, check permissions, and fetch roles
    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const userData = JSON.parse(storedUser);
                setCurrentUsername(userData.username || '');
                setCurrentUserRole(userData.role || 'user');
                setCurrentUserId(userData.id || null);
                // Fetch roles for dropdowns (MasterLayout handles route protection)
                fetchRoles();
                // Check if user can add users by fetching page access
                checkAddUserPermission();
            } catch (error) {
                console.error('Error parsing user data:', error);
            }
        }
    }, []);

    const checkAddUserPermission = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');
            
            if (!apiUrl || !token) return;
            
            const response = await fetch(`${apiUrl}/role-page-access/my-access`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    const addUserAccess = data.data.find(access => access.page_route === '/add-user');
                    setCanAddUser(addUserAccess ? addUserAccess.can_access === true : false);
                }
            }
        } catch (error) {
            console.error('Failed to check add user permission:', error);
            // Fallback: allow if user is admin
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                try {
                    const userData = JSON.parse(storedUser);
                    setCanAddUser(userData.role === 'admin');
                } catch (e) {
                    setCanAddUser(false);
                }
            }
        }
    };

    const fetchRoles = async () => {
        try {
            setLoadingRoles(true);
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/roles/active`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                setRoles(data.data);
            }
        } catch (err) {
            console.error('Failed to fetch roles:', err);
        } finally {
            setLoadingRoles(false);
        }
    };

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editFormData, setEditFormData] = useState({
        username: '',
        fullname: '',
        password: '',
        role_id: '',
        status: '',
    });
    const [editError, setEditError] = useState('');
    const [editValidationErrors, setEditValidationErrors] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);


    const fetchUsers = async (page = 1) => {
        try {
            setLoading(true);
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const params = new URLSearchParams({
                per_page: perPage,
                page: page,
            });

            if (statusFilter) {
                params.append('status', statusFilter);
            }

            if (searchQuery) {
                params.append('search', searchQuery);
            }

            const response = await fetch(`${apiUrl}/users?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch users: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setUsers(data.data);
                setPagination(data.pagination);
            } else {
                throw new Error(data.message || 'Failed to fetch users');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) {
            return;
        }

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/users/${userId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                fetchUsers(pagination.current_page);
            } else {
                alert(data.message || 'Failed to delete user');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to delete user');
        }
    };

    const handleEditClick = (user) => {
        setEditingUser(user);
        setEditFormData({
            username: user.username,
            fullname: user.fullname,
            password: '',
            role_id: user.role_id || '',
            status: user.status,
        });
        setEditError('');
        setEditValidationErrors({});
        setShowEditModal(true);
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear validation error for this field
        if (editValidationErrors[name]) {
            setEditValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditError('');
        setEditValidationErrors({});

        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('authToken');

        if (!apiUrl || !token) {
            setEditError('Missing API URL or authentication token');
            return;
        }

        try {
            setIsUpdating(true);

            // Only send password if it's not empty
            const updateData = {
                username: editFormData.username,
                fullname: editFormData.fullname,
                role_id: editFormData.role_id,
                status: editFormData.status,
            };

            if (editFormData.password) {
                updateData.password = editFormData.password;
            }

            const response = await fetch(`${apiUrl}/users/${editingUser.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updateData),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.errors) {
                    setEditValidationErrors(data.errors);
                }
                throw new Error(data.message || 'Failed to update user');
            }

            if (data.success) {
                setShowEditModal(false);
                fetchUsers(pagination.current_page);
            } else {
                throw new Error(data.message || 'Failed to update user');
            }
        } catch (err) {
            console.error(err);
            setEditError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingUser(null);
        setEditFormData({
            username: '',
            fullname: '',
            password: '',
            role_id: '',
            status: '',
        });
        setEditError('');
        setEditValidationErrors({});
    };


    const handleSearch = (e) => {
        e.preventDefault();
        fetchUsers(1);
    };

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [perPage, statusFilter]);

    useEffect(() => {
        const handleEscapeKey = (e) => {
            if (e.key === 'Escape') {
                if (showEditModal) {
                    handleCloseEditModal();
                }
            }
        };

        document.addEventListener('keydown', handleEscapeKey);
        return () => document.removeEventListener('keydown', handleEscapeKey);
    }, [showEditModal]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const { current_page, last_page } = pagination;

        for (let i = 1; i <= last_page; i++) {
            buttons.push(
                <li className="page-item" key={i}>
                    <button
                        className={`page-link fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                            current_page === i
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-200 text-secondary-light'
                        }`}
                        onClick={() => fetchUsers(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }
        return buttons;
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                        value={perPage}
                        onChange={(e) => setPerPage(Number(e.target.value))}
                    >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                    </select>
                    <form className="navbar-search" onSubmit={handleSearch}>
                        <input
                            type="text"
                            className="bg-base h-40-px w-auto"
                            name="search"
                            placeholder="Search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Icon icon="ion:search-outline" className="icon" />
                    </form>
                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>
                {canAddUser && (
                    <Link
                        to="/add-user"
                        className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                    >
                        <Icon
                            icon="ic:baseline-plus"
                            className="icon text-xl line-height-1"
                        />
                        Add New User
                    </Link>
                )}
            </div>
            <div className="card-body p-24">
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                        <th scope="col">S.L</th>
                                <th scope="col">Join Date</th>
                                        <th scope="col">Username</th>
                                        <th scope="col">Full Name</th>
                                        <th scope="col">Role</th>
                                <th scope="col" className="text-center">
                                    Status
                                </th>
                                <th scope="col" className="text-center">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                                    {users.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">
                                                No users found
                                </td>
                            </tr>
                                    ) : (
                                        users.map((user, index) => (
                                            <tr key={user.id}>
                                                <td>
                                                    {(pagination.current_page - 1) * perPage + index + 1}
                                </td>
                                                <td>{formatDate(user.created_at)}</td>
                                <td>
                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {user.username}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {user.fullname}
                                    </span>
                                </td>
                                                <td>
                                                    <span className="badge bg-primary-50 text-primary-600 px-12 py-4 radius-4 fw-medium text-sm text-capitalize">
                                                        {user.role_name || user.role}
                                    </span>
                                </td>
                                <td className="text-center">
                                                    <span
                                                        className={`${
                                                            user.status === 'active'
                                                                ? 'bg-success-focus text-success-600 border border-success-main'
                                                                : 'bg-neutral-200 text-neutral-600 border border-neutral-400'
                                                        } px-24 py-4 radius-4 fw-medium text-sm text-capitalize`}
                                                    >
                                                        {user.status}
                                    </span>
                                </td>
                                <td className="text-center">
                                    <div className="d-flex align-items-center gap-10 justify-content-center">
                                        {/* Helper function to normalize role for comparison */}
                                        {(() => {
                                            const normalizeRole = (role) => {
                                                if (!role) return '';
                                                return role.toString().toLowerCase().trim().replace(/[_\s]/g, '-');
                                            };
                                            
                                            const currentRoleNormalized = normalizeRole(currentUserRole);
                                            const userRoleNormalized = normalizeRole(user.role);
                                            
                                            // Determine if Edit button should be shown
                                            let canEdit = false;
                                            
                                            // Admin can edit all users
                                            if (currentRoleNormalized === 'admin') {
                                                canEdit = currentUsername === 'admin' || user.username !== 'admin';
                                            }
                                            // IT Manager can edit all users except admin
                                            else if (currentRoleNormalized === 'it-manager' || currentRoleNormalized === 'it_manager') {
                                                canEdit = user.username !== 'admin';
                                            }
                                            // Asset Manager can edit only asset-user role and itself
                                            else if (currentRoleNormalized === 'asset-manager' || currentRoleNormalized === 'asset_manager') {
                                                // Can edit if it's the current user OR if the user has asset-user role
                                                canEdit = (currentUserId && user.id === currentUserId) || 
                                                         userRoleNormalized === 'asset-user' || 
                                                         userRoleNormalized === 'asset_user';
                                            }
                                            
                                            return canEdit ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditClick(user)}
                                                        className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    >
                                                        <Icon icon="lucide:edit" className="menu-icon" />
                                                    </button>
                                                    {/* Admin and IT Manager can delete users (except admin user) */}
                                                    {(() => {
                                                        // Admin can delete all users (or all except admin if not super admin)
                                                        const adminCanDelete = currentRoleNormalized === 'admin' && 
                                                                              (currentUsername === 'admin' || user.username !== 'admin');
                                                        
                                                        // IT Manager can delete all users except admin
                                                        const itManagerCanDelete = (currentRoleNormalized === 'it-manager' || 
                                                                                    currentRoleNormalized === 'it_manager') && 
                                                                                   user.username !== 'admin';
                                                        
                                                        return (adminCanDelete || itManagerCanDelete) ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDelete(user.id)}
                                                                className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            >
                                                                <Icon
                                                                    icon="fluent:delete-24-regular"
                                                                    className="menu-icon"
                                                                />
                                                            </button>
                                                        ) : null;
                                                    })()}
                                                </>
                                            ) : null;
                                        })()}
                                    </div>
                                </td>
                            </tr>
                                        ))
                                    )}
                        </tbody>
                    </table>
                </div>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                            <span>
                                Showing {pagination.from || 0} to {pagination.to || 0} of{' '}
                                {pagination.total} entries
                            </span>
                    <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                        <li className="page-item">
                                    <button
                                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                        onClick={() => fetchUsers(pagination.current_page - 1)}
                                        disabled={pagination.current_page === 1}
                            >
                                <Icon icon="ep:d-arrow-left" className="" />
                                    </button>
                        </li>
                                {renderPaginationButtons()}
                        <li className="page-item">
                                    <button
                                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                        onClick={() => fetchUsers(pagination.current_page + 1)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    >
                                        <Icon icon="ep:d-arrow-right" className="" />
                                    </button>
                        </li>
                    </ul>
                </div>
                    </>
                )}
            </div>

            {/* Edit User Modal */}
            {showEditModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit User</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={handleCloseEditModal}
                                    disabled={isUpdating}
                                />
                            </div>
                            <form onSubmit={handleEditSubmit} noValidate>
                                <div className="modal-body">
                                    {editError && (
                                        <div className="alert alert-danger" role="alert">
                                            {editError}
                                        </div>
                                    )}
                                    <div className="mb-20">
                                        <label
                                            htmlFor="edit-username"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Username <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control radius-8 ${
                                                editValidationErrors.username ? 'is-invalid' : ''
                                            }`}
                                            id="edit-username"
                                            name="username"
                                            placeholder="Enter username"
                                            value={editFormData.username}
                                            onChange={handleEditInputChange}
                                            required
                                        />
                                        {editValidationErrors.username && (
                                            <div className="invalid-feedback d-block">
                                                {editValidationErrors.username[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="edit-fullname"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Full Name <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control radius-8 ${
                                                editValidationErrors.fullname ? 'is-invalid' : ''
                                            }`}
                                            id="edit-fullname"
                                            name="fullname"
                                            placeholder="Enter full name"
                                            value={editFormData.fullname}
                                            onChange={handleEditInputChange}
                                            required
                                        />
                                        {editValidationErrors.fullname && (
                                            <div className="invalid-feedback d-block">
                                                {editValidationErrors.fullname[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="edit-password"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Password <span className="text-secondary-light">(Leave blank to keep current)</span>
                                        </label>
                                        <input
                                            type="password"
                                            className={`form-control radius-8 ${
                                                editValidationErrors.password ? 'is-invalid' : ''
                                            }`}
                                            id="edit-password"
                                            name="password"
                                            placeholder="Enter new password (min 6 characters)"
                                            value={editFormData.password}
                                            onChange={handleEditInputChange}
                                        />
                                        {editValidationErrors.password && (
                                            <div className="invalid-feedback d-block">
                                                {editValidationErrors.password[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="edit-role_id"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Role <span className="text-danger-600">*</span>
                                        </label>
                                        <select
                                            className={`form-control radius-8 form-select ${
                                                editValidationErrors.role_id ? 'is-invalid' : ''
                                            }`}
                                            id="edit-role_id"
                                            name="role_id"
                                            value={editFormData.role_id}
                                            onChange={handleEditInputChange}
                                            required
                                            disabled={loadingRoles || !currentUserRole || (currentUserRole.toLowerCase() !== 'admin' && (currentUserRole || '').toLowerCase().replace(/\s+/g, '-') !== 'asset-manager')}
                                        >
                                            <option value="">Select Role...</option>
                                            {(() => {
                                                // Find Asset Manager role in the roles array to determine the correct slug/name
                                                const assetManagerRole = roles.find(r => {
                                                    const slug = (r.slug || '').toLowerCase().trim();
                                                    const name = (r.name || '').toLowerCase().trim();
                                                    return slug === 'asset-manager' || 
                                                           slug === 'asset_manager' ||
                                                           name === 'asset manager';
                                                });
                                                
                                                // Determine if current user is Asset Manager
                                                const userRole = (currentUserRole || '').toString();
                                                const userRoleLower = userRole.toLowerCase().trim();
                                                const normalizedUserRole = userRoleLower.replace(/[_\s]/g, '-');
                                                
                                                // Check if user is Asset Manager by comparing with role slug/name
                                                let isAssetManager = false;
                                                if (assetManagerRole) {
                                                    const managerSlug = (assetManagerRole.slug || '').toLowerCase().trim();
                                                    const managerName = (assetManagerRole.name || '').toLowerCase().trim();
                                                    isAssetManager = normalizedUserRole === managerSlug.replace(/[_\s]/g, '-') ||
                                                                   userRoleLower === managerName ||
                                                                   userRoleLower === managerSlug ||
                                                                   normalizedUserRole === 'asset-manager';
                                                } else {
                                                    // Fallback: check common variations
                                                    isAssetManager = normalizedUserRole === 'asset-manager' ||
                                                                   userRoleLower === 'asset manager' ||
                                                                   userRoleLower === 'asset_manager';
                                                }
                                                
                                                // Find Asset User role
                                                const assetUserRole = roles.find(r => {
                                                    const slug = (r.slug || '').toLowerCase().trim().replace(/[_\s]/g, '-');
                                                    const name = (r.name || '').toLowerCase().trim();
                                                    return slug === 'asset-user' || 
                                                           slug === 'asset_user' ||
                                                           name === 'asset user';
                                                });
                                                
                                                // Filter roles based on user type
                                                return roles
                                                    .filter((role) => {
                                                        // Super admin (username "admin") can select Admin role
                                                        if (role.slug === 'admin' || role.name === 'Admin') {
                                                            return currentUsername === 'admin';
                                                        }
                                                        
                                                        // Asset Manager can select Asset User and Asset Manager roles
                                                        if (isAssetManager) {
                                                            // Allow Asset User role
                                                            if (assetUserRole && role.id === assetUserRole.id) {
                                                                return true;
                                                            }
                                                            // Allow Asset Manager role (the user's own role)
                                                            if (assetManagerRole && role.id === assetManagerRole.id) {
                                                                return true;
                                                            }
                                                            // Don't allow any other roles
                                                            return false;
                                                        }
                                                        
                                                        // For admin users, show all other roles
                                                        return true;
                                                    })
                                                    .map((role) => (
                                                        <option key={role.id} value={role.id}>
                                                            {role.name}
                                                        </option>
                                                    ));
                                            })()}
                                        </select>
                                        {editValidationErrors.role_id && (
                                            <div className="invalid-feedback d-block">
                                                {editValidationErrors.role_id[0]}
                                            </div>
                                        )}
                                        {loadingRoles && (
                                            <small className="text-secondary-light">Loading roles...</small>
                                        )}
                                    </div>
                                    <div className="mb-20">
                                        <label
                                            htmlFor="edit-status"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                            Status <span className="text-danger-600">*</span>
                                        </label>
                                        <select
                                            className={`form-control radius-8 form-select ${
                                                editValidationErrors.status ? 'is-invalid' : ''
                                            }`}
                                            id="edit-status"
                                            name="status"
                                            value={editFormData.status}
                                            onChange={handleEditInputChange}
                                            required
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                        {editValidationErrors.status && (
                                            <div className="invalid-feedback d-block">
                                                {editValidationErrors.status[0]}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleCloseEditModal}
                                        disabled={isUpdating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isUpdating}
                                    >
                                        {isUpdating ? 'Updating...' : 'Update User'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default UsersListLayer;
